<template>
  <div class="py-2 px-3 max-w-360 m-auto w-full">
    <div class="w-302.5 m-auto">
      <Transition
        mode="out-in"
        enter-active-class="animate-in fade-in zoom-in-99 duration-200"
        leave-active-class="animate-out fade-out zoom-out-99 duration-200"
      >
        <PerformanceSkeleton v-if="skeletonVisible" key="skeleton" />
        <div
          v-else-if="phase === 'error'"
          key="error"
          class="flex h-40 items-center justify-center text-m-medium text-text-glass-default"
        >
          {{ $t('performance.loadFailed') }}
        </div>
        <div v-else-if="phase === 'ready'" key="content">
          <DeviceSettings
            :enabled-rf-packet-type="enabledRfPacketType"
            :rf-boost-switch="rfBoostSwitch"
            :rf-packet-type="rfPacketType"
            :report-rate="reportRate"
            :sensor-mode="sensorMode"
            :key-scan-rate="keyScanRate"
            :animate="animateValueChanges"
            @update:rf-boost-switch="setRfBoostSwitch"
            @update:rf-packet-type="setRfPacketType"
          />
          <PerformancePanel
            :perf-config="perfConfig"
            :sensor-mode="sensorMode"
            :key-scan-rate="keyScanRate"
            :report-rate="reportRate"
            :available-sensor-modes="availableSensorModes"
            :animate="animateValueChanges"
            @update:sensor-mode="setSensorMode"
            @update:key-scan-rate="setKeyScanRate"
            @update:report-rate="handleReportRateChange"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, watch } from 'vue'
  import DeviceSettings from './components/DeviceSettings.vue'
  import PerformancePanel from './components/PerformancePanel.vue'
  import PerformanceSkeleton from './components/PerformanceSkeleton.vue'
  import { useWallpaperStore } from '@/stores/wallpaper'
  import { useThemeStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import { usePerformance } from '../composables/usePerformance'
  import { useSkeletonHold } from '../composables/useSkeletonHold'
  import type { ReportRate } from '@rapoo/device-sdk'

  const wallpaperStore = useWallpaperStore()
  const themeStore = useThemeStore()
  const { isDark } = storeToRefs(themeStore)

  // ===================== SDK 对接 =====================
  const {
    enabledRfPacketType,
    rfBoostSwitch,
    rfPacketType,
    sensorMode,
    keyScanRate,
    reportRate,
    perfConfig,
    availableSensorModes,
    setRfBoostSwitch,
    setRfPacketType,
    setSensorMode,
    setKeyScanRate,
    setReportRate,
    phase,
    isReading,
    animateValueChanges,
  } = usePerformance()

  const showSkeleton = computed(() => phase.value === 'cold' && isReading.value)
  const skeletonVisible = useSkeletonHold(showSkeleton)

  /** 回报率变更时自动修正不兼容的模式（由 composable 内部处理） */
  function handleReportRateChange(value: ReportRate) {
    setReportRate(value)
  }

  // ===================== 背景参数（根据 sensorMode 动态调整） =====================

  /** 将 SensorMode 值映射为 UI 顺序索引（用于背景色计算）
   * SensorMode: 0=office, 1=LP(均衡), 2=HP(火力), 3=CodeGaming(超核), 4=竞技, 5=狂暴竞技
   * UI 顺序: 0=狂暴竞技(5), 1=竞技(4), 2=超核(3), 3=火力(2), 4=均衡(1), 5=办公(0)
   */
  const modeUiIndex = computed(() => {
    const modeToUiMap: Record<number, number> = { 5: 0, 4: 1, 3: 2, 2: 3, 1: 4, 0: 5 }
    return modeToUiMap[sensorMode.value] ?? 5
  })

  const auroraColorStops = computed(() => {
    if (isDark.value) {
      switch (modeUiIndex.value) {
        case 0:
          return ['#733F3B', '#831100', '#4B1801'] // 狂暴竞技
        case 1:
          return ['#705830', '#AD3E00', '#4B1801'] // 竞技
        case 2:
          return ['#B1BA36', '#FFD505', '#8A2900'] // 超核
        case 3:
          return ['#5E6E9C', '#3B1094', '#0E0E23'] // 火力
        case 4:
          return ['#428671', '#488551', '#171D22'] // 均衡
        case 5:
          return ['#48867C', '#253A80', '#0E0E23'] // 办公
        default:
          return ['#428671', '#488551', '#171D22']
      }
    } else {
      switch (modeUiIndex.value) {
        case 0:
          return ['#F8EEE4', '#FDB0B0', '#DBC8C1'] // 狂暴竞技
        case 1:
          return ['#F8F7E4', '#FFCDA7', '#DBC8C1'] // 竞技
        case 2:
          return ['#F2F8E4', '#FFFEA7', '#FFCA74'] // 超核
        case 3:
          return ['#E4F3F8', '#ADAEF4', '#B0C1E2'] // 火力
        case 4:
          return ['#E4F8F2', '#82E391', '#B0DBE2'] // 均衡
        case 5:
          return ['#E4F3F8', '#A1C3F4', '#B0DFE2'] // 办公
        default:
          return ['#E4F8F2', '#82E391', '#B0DBE2']
      }
    }
  })

  const applyOverride = () => {
    wallpaperStore.setPageOverride({
      component: 'Aurora',
      props: {
        colorStops: auroraColorStops.value,
        amplitude: 1,
        blend: 0.5,
        speed: 0.8,
        intensity: 1.0,
        style: {
          'background-color': isDark.value ? '#000000' : '#F0F0F0',
          transform: 'rotateX(180deg)',
        },
      },
    })
  }

  onMounted(() => {
    applyOverride()
  })

  watch([auroraColorStops, isDark], () => {
    applyOverride()
  })

  onUnmounted(() => {
    wallpaperStore.clearPageOverride()
  })
</script>

<style scoped></style>
